import {
  CodeIcon,
  CubeIcon,
  FontBoldIcon,
  FontItalicIcon,
  HeadingIcon,
  QuoteIcon,
  StrikethroughIcon,
  TextNoneIcon,
} from '@radix-ui/react-icons';
import { BubbleMenu, Editor } from '@tiptap/react';
import React from 'react';
import { Toolbar, ToolbarSeparator, ToolbarToggleGroup, ToolbarToggleItem } from './Toolbar';

type BubbleButtonProps = {
  icon: any;
  label: string;
  active: () => any;
  action: () => any;
};

type ToolBarItem =
  | {
      label: string;
      icon: JSX.Element;
      action: () => void;
      active: () => boolean;
    }
  | 'separator';

const makeControls = (editor: Editor): ToolBarItem[] => [
  {
    label: 'Heading 1',
    icon: <HeadingIcon />,
    action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    active: () => editor.isActive('heading', { level: 1 }),
  },
  {
    label: 'Heading 2',
    icon: <HeadingIcon width={12} />,
    action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    active: () => editor.isActive('heading', { level: 2 }),
  },

  'separator',

  {
    label: 'Bold',
    icon: <FontBoldIcon />,
    action: () => editor.chain().focus().toggleBold().run(),
    active: () => editor.isActive('bold'),
  },
  {
    label: 'Italic',
    icon: <FontItalicIcon />,
    action: () => editor.chain().focus().toggleItalic().run(),
    active: () => editor.isActive('italic'),
  },
  {
    label: 'Strike',
    icon: <StrikethroughIcon />,
    action: () => editor.chain().focus().toggleStrike().run(),
    active: () => editor.isActive('strike'),
  },
  {
    label: 'BlockQuote',
    icon: <QuoteIcon />,
    action: () => editor.chain().focus().toggleBlockquote().run(),
    active: () => editor.isActive('blockquote'),
  },

  // {
  //   label: 'Create Link',
  //   icon: <Link2Icon />,
  //   action: () => editor.chain().focus().setLink({ href: '', target: '_blank' }).run(),
  //   active: () => editor.isActive('link'),
  // },

  'separator',

  {
    label: 'Code',
    icon: <CodeIcon />,
    action: () => editor.chain().focus().toggleCode().run(),
    active: () => editor.isActive('code'),
  },
  {
    label: 'Code Block',
    icon: <CubeIcon />,
    action: () => editor.chain().focus().toggleCodeBlock().run(),
    active: () => editor.isActive('codeBlock'),
  },

  'separator',

  {
    label: 'Clear Formatting',
    icon: <TextNoneIcon />,
    action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
    active: () => false,
  },
];

const ToolbarButtonGroup = ({ label, action, icon, active }: BubbleButtonProps) => (
  <ToolbarToggleGroup
    type="single"
    onValueChange={() => action()}
    aria-label={label}
    value={active() ? label : ''}
  >
    <ToolbarToggleItem value={label} aria-label={label}>
      {icon}
    </ToolbarToggleItem>
  </ToolbarToggleGroup>
);

export const EditorControls = ({ editor }: { editor: Editor }) => {
  const controls = makeControls(editor).map((definition: ToolBarItem, idx) =>
    definition === 'separator' ? (
      <ToolbarSeparator key={idx} />
    ) : (
      <ToolbarButtonGroup key={definition.label} {...definition} />
    )
  );

  return (
    <BubbleMenu editor={editor} tippyOptions={{ duration: 100, appendTo: 'parent' }}>
      <Toolbar>{controls}</Toolbar>
    </BubbleMenu>
  );
};
